import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Input,
  FormField,
  PasswordInput,
  SubmitButton,
  SubmittingHint,
  useSubmittingMutation,
} from "@/design-system/components/ui";
import { emailFieldError, isValidEmail } from "@/lib/emailValidator";
import { PortalShell } from "../components/PortalShell";
import { portalAuthApi } from "../api/auth";
import { PortalApiError } from "../api/client";

// External Intake & Collection v1.0 PR 9b §8 — broker password reset.
// Two modes:
//   - no token in URL → request a reset email (always neutral response)
//   - ?token=… → set a new password using the reset token

export function BrokerPasswordResetPage() {
  const [params] = useSearchParams();
  const token = params.get("token");
  return token ? <ConfirmReset token={token} /> : <RequestReset />;
}

function RequestReset() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { submit, pending } = useSubmittingMutation(
    () => portalAuthApi.passwordResetRequest(email.trim().toLowerCase()),
    {
      onSuccess: () => setSent(true),
      onError: (err) => {
        const msg = err instanceof PortalApiError ? err.message : err instanceof Error ? err.message : "Request failed.";
        setError(msg);
      },
    },
  );

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!email.trim()) {
      setError("Enter your email.");
      return;
    }
    if (!isValidEmail(email)) {
      setError("Enter a valid email address.");
      return;
    }
    void submit();
  }

  if (sent) {
    return (
      <PortalShell pageTitle="Reset password">
        <h1 className="text-h2 font-display text-onyx m-0">Check your email</h1>
        <p className="text-small text-onyx mt-space-3">
          If that address is on file, we've sent a password reset link.
          Open it on this device to set a new password.
        </p>
      </PortalShell>
    );
  }

  return (
    <PortalShell pageTitle="Reset password">
      <h1 className="text-h2 font-display text-onyx m-0">Reset your password</h1>
      <form onSubmit={handleSubmit} className="mt-space-5 flex flex-col gap-space-4" noValidate>
        <FormField
          label="Email"
          required
          error={email ? emailFieldError(email, { required: true }) : undefined}
        >
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={pending}
            invalid={!!email && !isValidEmail(email)}
          />
        </FormField>
        {error && <p className="text-small text-status-error m-0" role="alert">{error}</p>}
        <SubmitButton type="submit" pending={pending} pendingLabel="Sending…">
          Send reset link
        </SubmitButton>
        <SubmittingHint pending={pending} />
      </form>
      <p className="text-caption text-gray-mid mt-space-4">
        <Link to="/portal/login" className="underline">Back to sign-in</Link>
      </p>
    </PortalShell>
  );
}

function ConfirmReset({ token }: { token: string }) {
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { submit, pending } = useSubmittingMutation(
    () => portalAuthApi.passwordResetConfirm(token, pw),
    {
      onSuccess: () => setDone(true),
      onError: (err) => {
        const msg = err instanceof PortalApiError ? err.message : err instanceof Error ? err.message : "Reset failed.";
        setError(msg);
      },
    },
  );

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (pw.length < 12) {
      setError("Password must be at least 12 characters.");
      return;
    }
    if (pw !== pw2) {
      setError("Passwords don't match.");
      return;
    }
    void submit();
  }

  if (done) {
    return (
      <PortalShell pageTitle="Password updated">
        <h1 className="text-h2 font-display text-onyx m-0">Password updated</h1>
        <p className="text-small text-onyx mt-space-3">You can now sign in with your new password.</p>
        <p className="mt-space-4">
          <Link to="/portal/login" className="underline">Go to sign-in</Link>
        </p>
      </PortalShell>
    );
  }

  return (
    <PortalShell pageTitle="Set a new password">
      <h1 className="text-h2 font-display text-onyx m-0">Set a new password</h1>
      <form onSubmit={handleSubmit} className="mt-space-5 flex flex-col gap-space-4" noValidate>
        <FormField label="New password" required helperText="At least 12 characters.">
          <PasswordInput value={pw} onChange={(e) => setPw(e.target.value)} disabled={pending} />
        </FormField>
        <FormField label="Confirm new password" required>
          <PasswordInput value={pw2} onChange={(e) => setPw2(e.target.value)} disabled={pending} />
        </FormField>
        {error && <p className="text-small text-status-error m-0" role="alert">{error}</p>}
        <SubmitButton type="submit" pending={pending} pendingLabel="Saving…">
          Update password
        </SubmitButton>
        <SubmittingHint pending={pending} />
      </form>
    </PortalShell>
  );
}
